// components/team_servicing/sections/EmployeeSummary.jsx
// WITH VISIBILITY SETTINGS - Refactored with shared utilities

import React, { useState, useMemo, useEffect, useRef, memo, useCallback } from 'react';
import { usePagePreferences } from '../../../hooks/useUserPreference';
import { 
  ChevronUpIcon, 
  ChevronDownIcon,
  UserIcon,
  ClockIcon,
  ChartBarIcon,
  CalendarIcon,
  Cog6ToothIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';
import MetricCard from '../../common/MetricCard';

// Import shared utilities
import { formatHours, formatPercentage, getProductivityColor } from '../../../utils/dashboardUtils';
import { TIME_OFF_CATEGORIES } from '../../../utils/dashboardConstants';

// ============================================================================
// MEMOIZED COMPONENTS - DEFINED OUTSIDE to prevent re-mounting
// ============================================================================

// Memoized employee item to prevent unnecessary re-renders
const EmployeeItem = memo(({ employeeName, isVisible, onClick }) => (
  <div
    className="flex items-center px-3 py-2 hover:bg-gray-50 rounded cursor-pointer transition-colors"
    onClick={onClick}
  >
    <input
      type="checkbox"
      checked={isVisible}
      readOnly
      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 pointer-events-none"
    />
    <span className="ml-3 text-sm text-gray-900 flex-1">{employeeName}</span>
    {isVisible ? (
      <EyeIcon className="h-4 w-4 text-green-500" />
    ) : (
      <EyeSlashIcon className="h-4 w-4 text-gray-400" />
    )}
  </div>
));

// Visibility Settings Modal - DEFINED OUTSIDE to prevent re-mounting
const VisibilitySettings = memo(({
  allEmployeeNames,
  localHiddenEmployees,
  savedHiddenEmployees,
  hasUnsavedChanges,
  isSaving,
  onClose,
  onToggleEmployee,
  onSelectAll,
  onSave,
  onCancel
}) => {
  const scrollContainerRef = useRef(null);

  const allVisible = localHiddenEmployees.size === 0;
  const someHidden = localHiddenEmployees.size > 0 && localHiddenEmployees.size < allEmployeeNames.length;

  return (
    <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
      {/* Save/Cancel at top */}
      {hasUnsavedChanges && (
        <div className="p-3 border-b border-gray-200 bg-blue-50">
          <div className="flex gap-2">
            <button
              onClick={onSave}
              disabled={isSaving}
              className="flex-1 px-2 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={onCancel}
              disabled={isSaving}
              className="flex-1 px-2 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      
      {/* Close button in top right */}
      <div className="p-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">
            {allEmployeeNames.length - localHiddenEmployees.size}/{allEmployeeNames.length} visible
          </span>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          >
            ×
          </button>
        </div>
      </div>
      
      {/* Select All Checkbox at top of list */}
      <div className="px-3 pt-2 pb-1">
        <label className="flex items-center px-3 py-2 bg-gray-50 rounded cursor-pointer hover:bg-gray-100 transition-colors">
          <input
            type="checkbox"
            checked={allVisible}
            ref={(el) => {
              if (el) el.indeterminate = someHidden;
            }}
            onChange={onSelectAll}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="ml-3 text-sm font-medium text-gray-900">
            {allVisible ? 'Unselect All' : 'Select All'}
          </span>
        </label>
      </div>
      
      {/* Employee list - scroll preserved because component doesn't re-mount */}
      <div ref={scrollContainerRef} className="max-h-96 overflow-y-auto px-3 pb-3">
        {allEmployeeNames.map((employeeName) => {
          const isVisible = !localHiddenEmployees.has(employeeName);
          return (
            <EmployeeItem
              key={employeeName}
              employeeName={employeeName}
              isVisible={isVisible}
              onClick={() => onToggleEmployee(employeeName)}
            />
          );
        })}
      </div>
      
      {/* Info footer */}
      {!hasUnsavedChanges && (
        <div className="px-3 py-2 border-t border-gray-200 bg-gray-50">
          <p className="text-xs text-gray-600">
            {savedHiddenEmployees.length > 0 
              ? `${savedHiddenEmployees.length} employee${savedHiddenEmployees.length > 1 ? 's' : ''} hidden from table`
              : 'All employees visible in table'}
          </p>
        </div>
      )}
    </div>
  );
});

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const EmployeeSummary = ({ data, summaryStats, onCardClick }) => {
  // UI STATE
  const [sortConfig, setSortConfig] = useState({ key: 'totalHours', direction: 'desc' });
  const [showProductivityFilter, setShowProductivityFilter] = useState('all');
  const [showVisibilitySettings, setShowVisibilitySettings] = useState(false);
  const [showEmployeeDetails, setShowEmployeeDetails] = useState(false);
  
  // LOCAL STATE - for fast UI updates before saving
  const [localHiddenEmployees, setLocalHiddenEmployees] = useState(new Set());
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // PERSISTED STATE - from backend
  const [preferences, setPreference, , prefsLoading, prefsError, prefsInitialized] = usePagePreferences(
    'employee_summary',
    {
      hidden_employees: { hidden_employees: [] }
    }
  );

  // Extract saved preferences
  const savedHiddenEmployees = preferences.hidden_employees?.hidden_employees || [];
  
  // Initialize local state from saved preferences once loaded
  const initializedRef = useRef(false);
  
  useEffect(() => {
    if (prefsInitialized && !hasUnsavedChanges && !initializedRef.current) {
      setLocalHiddenEmployees(new Set(savedHiddenEmployees));
      initializedRef.current = true;
    }
  }, [prefsInitialized, hasUnsavedChanges, savedHiddenEmployees]);

  // Use SAVED state for table display
  const hiddenEmployees = useMemo(() => new Set(savedHiddenEmployees), [savedHiddenEmployees]);

  // Process employee data
  const employeeData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const employeeMap = new Map();

    data.forEach(entry => {
      const employee = entry.employee;
      if (!employee) return;

      if (!employeeMap.has(employee)) {
        employeeMap.set(employee, {
          employee: employee,
          totalHours: 0,
          clientHours: 0,
          nonClientHours: 0,
          timeOffHours: 0,
          billableHours: 0,
          totalRevenue: 0,
          customers: new Set(),
          entries: 0,
          activities: new Set()
        });
      }
      
      const empData = employeeMap.get(employee);
      const hours = parseFloat(entry.total_hours || 0);
      const amount = parseFloat(entry.fees || 0);

      empData.totalHours += hours;
      empData.totalRevenue += amount;
      empData.entries += 1;

      if (TIME_OFF_CATEGORIES.includes(entry.category)) {
        empData.timeOffHours += hours;
      }

      if (entry.is_client_work) {
        empData.clientHours += hours;
      } else {
        empData.nonClientHours += hours;
      }

      if (entry.is_client_work) {
        empData.billableHours += hours;
      }

      if (entry.customer) {
        empData.customers.add(entry.customer);
      }

      if (entry.activity) {
        empData.activities.add(entry.activity);
      }
    });

    return Array.from(employeeMap.values()).map(emp => {
      const availableHours = emp.totalHours - emp.timeOffHours;
      const productivityRate = availableHours > 0 ? emp.clientHours / availableHours : 0;
      
      return {
        ...emp,
        availableHours,
        customerCount: emp.customers.size,
        activityCount: emp.activities.size,
        utilizationRate: emp.totalHours > 0 ? emp.clientHours / emp.totalHours : 0,
        productivityRate,
        billingRate: emp.totalHours > 0 ? emp.billableHours / emp.totalHours : 0,
        avgRate: emp.totalHours > 0 ? emp.totalRevenue / emp.totalHours : 0,
        productivityScore: availableHours > 0 ? 
          ((emp.clientHours / availableHours) * 0.6) + ((emp.billableHours / availableHours) * 0.4) : 0
      };
    });
  }, [data]);

  // Get all unique employee names
  const allEmployeeNames = useMemo(() => {
    return employeeData.map(e => e.employee).sort();
  }, [employeeData]);

  // Toggle employee visibility (LOCAL ONLY - fast)
  const toggleEmployeeVisibility = useCallback((employeeName) => {
    setLocalHiddenEmployees(prev => {
      const newSet = new Set(prev);
      if (newSet.has(employeeName)) {
        newSet.delete(employeeName);
      } else {
        newSet.add(employeeName);
      }
      return newSet;
    });
    setHasUnsavedChanges(true);
  }, []);

  // Show all employees (LOCAL)
  const showAllEmployees = useCallback(() => {
    setLocalHiddenEmployees(new Set());
    setHasUnsavedChanges(true);
  }, []);

  // Hide all employees (LOCAL)
  const hideAllEmployees = useCallback(() => {
    setLocalHiddenEmployees(new Set(allEmployeeNames));
    setHasUnsavedChanges(true);
  }, [allEmployeeNames]);

  // Handle select all toggle
  const handleSelectAll = useCallback(() => {
    if (localHiddenEmployees.size === 0) {
      hideAllEmployees();
    } else {
      showAllEmployees();
    }
  }, [localHiddenEmployees.size, hideAllEmployees, showAllEmployees]);

  // Save to backend
  const saveHiddenEmployees = useCallback(async () => {
    setIsSaving(true);
    try {
      await setPreference('hidden_employees', { 
        hidden_employees: Array.from(localHiddenEmployees) 
      });
      setHasUnsavedChanges(false);
      setShowVisibilitySettings(false);
    } catch (err) {
      console.error('Error saving hidden employees:', err);
    } finally {
      setIsSaving(false);
    }
  }, [localHiddenEmployees, setPreference]);

  // Cancel changes
  const cancelChanges = useCallback(() => {
    setLocalHiddenEmployees(new Set(savedHiddenEmployees));
    setHasUnsavedChanges(false);
    setShowVisibilitySettings(false);
  }, [savedHiddenEmployees]);

  // Filter employees based on productivity AND visibility
  const filteredEmployees = useMemo(() => {
    let filtered = employeeData.filter(emp => !hiddenEmployees.has(emp.employee));
    
    if (showProductivityFilter === 'all') return filtered;
    
    return filtered.filter(emp => {
      const prodRate = emp.productivityRate;
      if (showProductivityFilter === 'high') return prodRate >= 0.75;
      if (showProductivityFilter === 'medium') return prodRate >= 0.5 && prodRate < 0.75;
      if (showProductivityFilter === 'low') return prodRate < 0.5;
      return true;
    });
  }, [employeeData, showProductivityFilter, hiddenEmployees]);

  // Sort employees - Freelance always last
  const sortedEmployees = useMemo(() => {
    let sorted = [...filteredEmployees];
    
    if (sortConfig.key) {
      sorted.sort((a, b) => {
        if (a.employee === 'Freelance') return 1;
        if (b.employee === 'Freelance') return -1;
        
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    } else {
      sorted.sort((a, b) => {
        if (a.employee === 'Freelance') return 1;
        if (b.employee === 'Freelance') return -1;
        return a.employee.localeCompare(b.employee);
      });
    }
    
    return sorted;
  }, [filteredEmployees, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Summary statistics - Uses ALL employees (not filtered by hidden)
  const totalEmployees = employeeData.length;
  const employeeCount = employeeData.filter(emp => emp.employee !== 'Freelance').length;
  const freelanceCount = employeeData.filter(emp => emp.employee === 'Freelance').length;
  
  const totalClientHours = employeeData.reduce((sum, emp) => sum + emp.clientHours, 0);
  const totalAvailableHours = employeeData.reduce((sum, emp) => sum + emp.availableHours, 0);
  const overallProductivity = totalAvailableHours > 0 ? totalClientHours / totalAvailableHours : 0;
  
  const highPerformers = employeeData.filter(emp => emp.productivityRate >= 0.75).length;
  const totalTimeOff = employeeData.reduce((sum, emp) => sum + emp.timeOffHours, 0);

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
        <p className="mt-2">No employee data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        {/* Summary Metric Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Total Staffs"
            value={totalEmployees}
            changeLabel={`${employeeCount} Employees, ${freelanceCount} Freelance`}
            icon={UserIcon}
            loading={false}
            isClickable={false}
          />

          <MetricCard
            title="Overall Productivity"
            value={formatPercentage(overallProductivity * 100)}
            changeLabel="Client / Available Hrs"
            icon={ChartBarIcon}
            loading={false}
            isClickable={false}
          />

          <MetricCard
            title="High Performers"
            value={highPerformers}
            changeLabel="≥75% productivity"
            icon={ClockIcon}
            loading={false}
            isClickable={false}
          />

          <MetricCard
            title="Total Time Off"
            value={`${formatHours(totalTimeOff)}h`}
            changeLabel="Vacation, Sick, Holiday"
            icon={CalendarIcon}
            loading={false}
            isClickable={false}
          />
        </div>

        {/* Arrow toggle indicator */}
        <div 
          className="mt-6 pt-4 border-t border-gray-100 cursor-pointer"
          onClick={() => setShowEmployeeDetails(!showEmployeeDetails)}
        >
          <div className="text-xs text-gray-600 flex items-center justify-end gap-2 hover:text-gray-900 transition-colors">
            <span>Click to {showEmployeeDetails ? 'hide' : 'view'} employee details</span>
            <div className="group flex items-center justify-center h-6 w-6 rounded-full border-2 border-gray-400 hover:border-primary-500 hover:bg-primary-500 hover:scale-110 transition-all">
              {showEmployeeDetails ? (
                <ArrowUpIcon className="h-4 w-4 text-gray-400 group-hover:text-white transition-colors" />
              ) : (
                <ArrowDownIcon className="h-4 w-4 text-gray-400 group-hover:text-white transition-colors" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Employee Details Section - Collapsible */}
      {showEmployeeDetails && (
        <>
          {/* Filters and Controls */}
          <div className="flex flex-wrap gap-4 items-center justify-between bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div className="flex items-center space-x-4">
              <label className="text-sm font-medium text-gray-700">Filter by Productivity:</label>
              <select
                value={showProductivityFilter}
                onChange={(e) => setShowProductivityFilter(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Employees ({totalEmployees})</option>
                <option value="high">High (≥75%)</option>
                <option value="medium">Medium (50-74%)</option>
                <option value="low">Low (&lt;50%)</option>
              </select>
            </div>
            
            <div className="text-sm text-gray-600">
              Showing <span className="font-semibold">{filteredEmployees.length}</span> of <span className="font-semibold">{totalEmployees}</span> employees
              {hiddenEmployees.size > 0 && (
                <span className="text-gray-500"> ({hiddenEmployees.size} hidden)</span>
              )}
            </div>
          </div>

          {/* Employee Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {/* Table Header with Visibility Settings */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Employee Details</h3>
              <div className="relative">
                <button
                  onClick={() => setShowVisibilitySettings(!showVisibilitySettings)}
                  className="p-2 text-white hover:bg-blue-700 rounded-lg transition-colors relative"
                  title="Employee Visibility Settings"
                >
                  <Cog6ToothIcon className="h-5 w-5" />
                  {savedHiddenEmployees.length > 0 && (
                    <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 rounded-full text-xs flex items-center justify-center text-white">
                      {savedHiddenEmployees.length}
                    </span>
                  )}
                </button>
                {showVisibilitySettings && (
                  <VisibilitySettings
                    allEmployeeNames={allEmployeeNames}
                    localHiddenEmployees={localHiddenEmployees}
                    savedHiddenEmployees={savedHiddenEmployees}
                    hasUnsavedChanges={hasUnsavedChanges}
                    isSaving={isSaving}
                    onClose={() => setShowVisibilitySettings(false)}
                    onToggleEmployee={toggleEmployeeVisibility}
                    onSelectAll={handleSelectAll}
                    onSave={saveHiddenEmployees}
                    onCancel={cancelChanges}
                  />
                )}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleSort('employee')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Employee</span>
                        {sortConfig.key === 'employee' && (
                          sortConfig.direction === 'asc' ? 
                            <ChevronUpIcon className="w-4 h-4" /> : 
                            <ChevronDownIcon className="w-4 h-4" />
                        )}
                      </div>
                    </th>
                    
                    <th 
                      className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleSort('totalHours')}
                    >
                      <div className="flex items-center justify-center space-x-1">
                        <span>Total Hours</span>
                        {sortConfig.key === 'totalHours' && (
                          sortConfig.direction === 'asc' ? 
                            <ChevronUpIcon className="w-4 h-4" /> : 
                            <ChevronDownIcon className="w-4 h-4" />
                        )}
                      </div>
                    </th>
                    
                    <th 
                      className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleSort('clientHours')}
                    >
                      <div className="flex items-center justify-center space-x-1">
                        <span>Client Hours</span>
                        {sortConfig.key === 'clientHours' && (
                          sortConfig.direction === 'asc' ? 
                            <ChevronUpIcon className="w-4 h-4" /> : 
                            <ChevronDownIcon className="w-4 h-4" />
                        )}
                      </div>
                    </th>
                    
                    <th 
                      className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleSort('productivityRate')}
                    >
                      <div className="flex items-center justify-center space-x-1">
                        <span>Productivity</span>
                        {sortConfig.key === 'productivityRate' && (
                          sortConfig.direction === 'asc' ? 
                            <ChevronUpIcon className="w-4 h-4" /> : 
                            <ChevronDownIcon className="w-4 h-4" />
                        )}
                      </div>
                    </th>
                    
                    <th 
                      className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleSort('customerCount')}
                    >
                      <div className="flex items-center justify-center space-x-1">
                        <span>Customers Served</span>
                        {sortConfig.key === 'customerCount' && (
                          sortConfig.direction === 'asc' ? 
                            <ChevronUpIcon className="w-4 h-4" /> : 
                            <ChevronDownIcon className="w-4 h-4" />
                        )}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedEmployees.map((employee, index) => (
                    <tr key={employee.employee} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className={`h-10 w-10 rounded-full ${
                              employee.employee === 'Freelance' 
                                ? 'bg-gradient-to-br from-purple-400 to-purple-600' 
                                : 'bg-gradient-to-br from-blue-400 to-blue-600'
                            } flex items-center justify-center shadow-sm`}>
                              <span className="text-sm font-semibold text-white">
                                {employee.employee.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {employee.employee}
                              {employee.employee === 'Freelance' && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                  Freelance
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500">{employee.entries} time entries</div>
                          </div>
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                        <div className="font-medium">{formatHours(employee.totalHours)}h</div>
                        <div className="text-xs text-gray-500">
                          <span className="text-blue-600 font-medium">{formatHours(employee.availableHours)}h</span> Available
                          {employee.timeOffHours > 0 && (
                            <> · {formatHours(employee.timeOffHours)}h PTO</>
                          )}
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                        <div className="font-medium">{formatHours(employee.clientHours)}h</div>
                        <div className="text-xs text-gray-500">{formatHours(employee.nonClientHours)}h internal</div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getProductivityColor(employee.productivityRate)}`}>
                          {formatPercentage(employee.productivityRate * 100)}
                        </span>
                        <div className="text-xs text-gray-500 mt-1">
                          {formatHours(employee.clientHours)} / {formatHours(employee.availableHours)}
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                        <div className="font-medium">{employee.customerCount}</div>
                        <div className="text-xs text-gray-500">{employee.activityCount} activities</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {filteredEmployees.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <UserIcon className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                <p className="text-lg font-medium">No employees match the current filter</p>
                <p className="text-sm mt-1">Try adjusting your filter criteria</p>
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-blue-800">Productivity Calculation</h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p><strong>Overall Productivity = Total Client Hours ÷ Total Available Hours</strong></p>
                  <p className="mt-1">Available Hours = Total Hours - Time Off (Vacation, Sick, Holiday, PTO)</p>
                  <p className="mt-1">Current: {formatHours(totalClientHours)} ÷ {formatHours(totalAvailableHours)} = {formatPercentage(overallProductivity * 100)}</p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EmployeeSummary;